import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_CONTENT_INSET, OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import { OscarSuggestion } from '../../types/AIAgentTypes';
import OscarMark from '../shared/OscarMark';
import OscarSuggestionChip from '../shared/OscarSuggestionChip';
import { useOscarTheme } from '../../context/OscarRoleContext';

interface OscarEmptyStateProps {
	/** Figma: "Hello XYZ," — greeting line, rendered in the muted weight. */
	greeting: string;
	/** Figma: "How can I help you today?" */
	prompt?: string;
	suggestions?: OscarSuggestion[];
	onSuggestionClick?: (suggestion: OscarSuggestion) => void;
	className?: string;
}

/**
 * Figma node 14998:125314 — vertically centered mark-in-a-circle, two-line
 * greeting, then one full-width suggestion chip per starter prompt.
 */
export const OscarEmptyState = ({
	greeting,
	prompt = 'How can I help you today?',
	suggestions = [],
	onSuggestionClick,
	className,
}: OscarEmptyStateProps) => {
	const theme = useOscarTheme();
	return (
		<div
			className={cn(
				'flex w-full flex-1 flex-col items-center justify-center gap-s overflow-y-auto',
				className
			)}
		>
			<div className={cn('flex w-full flex-col justify-center gap-l', OSCAR_CONTENT_INSET)}>
				<div className="flex flex-col items-start gap-s">
					{/* 56px mark + 8px padding = the 72px ring, built from tokens. */}
					<div
						className={cn(
							'flex w-fit shrink-0 items-center justify-center rounded-full p-xs',
							theme.accentSoft
						)}
					>
						<OscarMark size={OSCAR_MARK_SIZE.hero} />
					</div>
					<div className="flex flex-col">
						<span className="h3regular text-character-secondary">{greeting}</span>
						<span className="h3medium text-grey-10">{prompt}</span>
					</div>
				</div>
				{suggestions.map((suggestion) => (
					<OscarSuggestionChip
						key={suggestion.id}
						label={suggestion.label}
						onClick={onSuggestionClick ? () => onSuggestionClick(suggestion) : undefined}
					/>
				))}
			</div>
		</div>
	);
};

export default OscarEmptyState;
